import React, { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const KEY = "tn_install_dismissed";
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(KEY) || !isMobile()) return;
    if (ios) { setShow(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [ios]);

  const dismiss = () => { localStorage.setItem(KEY, "1"); setShow(false); };
  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferred(null);
  };

  if (!show) return null;
  return (
    <div className="md:hidden fixed inset-x-3 z-40 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] bg-white border border-slate-200 shadow-lg rounded-2xl p-3.5 flex gap-3 items-start animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="install-prompt">
      <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0 ring-1 ring-slate-200" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Install Thinkneural</p>
        {ios ? (
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed" data-testid="install-ios-steps">
            Tap <Share className="inline w-3.5 h-3.5 -mt-0.5 text-indigo-600" /> <b>Share</b>, then{" "}
            <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5 text-indigo-600" /> <b>Add to Home Screen</b>.
          </p>
        ) : (
          <>
            <p className="text-xs text-slate-500 mt-0.5">Add it to your home screen for a full-screen app.</p>
            <Button size="sm" onClick={install} className="mt-2 h-8 bg-indigo-600 hover:bg-indigo-700" data-testid="install-app-button">
              <Download className="w-3.5 h-3.5 mr-1.5" />Install app
            </Button>
          </>
        )}
      </div>
      <button onClick={dismiss} className="w-8 h-8 -m-1 rounded-lg flex items-center justify-center text-slate-400 active:bg-slate-100" data-testid="install-dismiss-button" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
